import { NodeStyle } from '@view-model/models/sticky/StickyNodeView';
import type { ThemeColor } from '@view-model/models/common/color';
import type { StickyZoneShape, StickyZoneTitlePosition } from '@view-model/models/sticky/StickyZoneView';
import { StickyZoneStyle } from '@view-model/models/sticky/StickyZoneView';
import { LinkStyle } from '@view-model/models/sticky/StickyLink';
import type { LinkColor, LinkLineStyle, LinkMarkStyle } from '@model-framework/link';
import type { AnnotationLinkStyle } from 'balus-common';

/**
 * モデル要素の新規作成時に適用するスタイルを管理、提供します。
 *
 * スタイルの変更イベントを受け取り、それを元に次回の新規作成時のスタイルを返します。
 */
export class NewElementStyleProvider {
  private _nodeStyle: NodeStyle;
  private _zoneStyle: StickyZoneStyle;
  private _linkStyle: LinkStyle;
  private _annotationLinkStyle: AnnotationLinkStyle = 'wedge';

  constructor() {
    this._nodeStyle = NodeStyle.buildNew();
    this._zoneStyle = StickyZoneStyle.buildNew();
    this._linkStyle = LinkStyle.buildNew();
  }

  /**
   * ノードの色変更が起きたことをこのインスタンスに知らせて次回のスタイルに反映されるようにする。
   * @param color
   */
  nodeColorChanged(color: ThemeColor): void {
    this._nodeStyle = this._nodeStyle.withThemeColor(color);
  }

  /**
   * ノードスタイルを返す。
   */
  nodeStyle(): NodeStyle {
    return this._nodeStyle;
  }

  /**
   * ゾーンの色変更が起きたことをこのインスタンスに知らせて次回のスタイルに反映されるようにする。
   * @param color
   */
  zoneColorChanged(color: ThemeColor): void {
    this._zoneStyle = this._zoneStyle.withThemeColor(color);
  }

  /**
   * ゾーンの形状変更が起きたことをこのインスタンスに知らせて次回のスタイルに反映されるようにする。
   * @param shape
   */
  zoneShapeChanged(shape: StickyZoneShape): void {
    this._zoneStyle = this._zoneStyle.withShape(shape);
  }

  /**
   * ゾーンのタイトル位置変更が起きたことをこのインスタンスに知らせて次回のスタイルに反映されるようにする。
   * @param titlePosition
   */
  zoneTitlePositionChanged(titlePosition: StickyZoneTitlePosition | null): void {
    this._zoneStyle = this._zoneStyle.withTitlePosition(titlePosition);
  }

  /**
   * ゾーンスタイルを返す。
   */
  zoneStyle(): StickyZoneStyle {
    return this._zoneStyle;
  }

  /**
   * リンクの色変更が起きたことをこのインスタンスに知らせて次回のスタイルに反映されるようにする。
   */
  linkColorChanged(color: LinkColor): void {
    this._linkStyle = this._linkStyle.withColor(color);
  }

  /**
   * リンクの線スタイル変更が起きたことをこのインスタンスに知らせて次回のスタイルに反映されるようにする。
   */
  linkLineStyleChanged(lineStyle: LinkLineStyle): void {
    this._linkStyle = this._linkStyle.withLineStyle(lineStyle);
  }

  /**
   * リンクの矢印マーク変更が起きたことをこのインスタンスに知らせて次回のスタイルに反映されるようにする。
   */
  linkMarkStyleChanged(markStyle: LinkMarkStyle): void {
    this._linkStyle = this._linkStyle.withMarkStyle(markStyle);
  }

  /**
   * リンクのスタイルを返す
   */
  linkStyle(): LinkStyle {
    return this._linkStyle;
  }

  annotationLinkStyleChanged(style: AnnotationLinkStyle): void {
    this._annotationLinkStyle = style;
  }

  annotationLinkStyle(): AnnotationLinkStyle {
    return this._annotationLinkStyle;
  }
}
